import { defineToolCallRenderer, ToolCallStatus } from "@copilotkit/react-core/v2";
import type { ReactElement } from "react";
import type { z } from "zod";

import type { CopilotToolName } from "@/constants/copilot/copilot-constants";
import { parseToolResult } from "@/lib/copilot/parse-tool-result";
import type { Appointment } from "@/types/appointments/api-types/appointment-types";
import type { StaffAvailability } from "@/types/appointments/api-types/availability-types";
import type { ClientProfile } from "@/types/copilot/copilot-types";
import type { StaffServicesSummary } from "@/types/staff/api-types/staff-services-summary-types";
import {
  availabilityArgsSchema,
  dateRangeArgsSchema,
  noArgsSchema,
} from "@/zod-schemas/copilot/tool-args-schemas";

import { ClientCard } from "./client-card";
import { CopilotStep } from "./copilot-step";
import { DiaryCard } from "./diary-card";
import { FreeTimeCard } from "./free-time-card";
import { PriceListCard } from "./price-list-card";

function toolCard<S extends z.ZodType, T>(
  name: CopilotToolName,
  args: S,
  toCard: (data: T, args: z.infer<S>) => ReactElement
) {
  return defineToolCallRenderer({
    name,
    args,
    render: (call) => {
      const data = call.status === ToolCallStatus.Complete ? parseToolResult<T>(call.result) : undefined;
      // A complete call has its full arguments; CopilotKit types them by the schema's Standard Schema output,
      // which for a Zod schema is z.infer.
      return data !== undefined ? (
        toCard(data, call.args as z.infer<S>)
      ) : (
        <CopilotStep name={name} complete={call.status === ToolCallStatus.Complete} />
      );
    },
  });
}

export const COPILOT_TOOL_RENDERERS = [
  toolCard("get_client", noArgsSchema, (profile: ClientProfile) => <ClientCard profile={profile} />),
  toolCard("list_appointments", dateRangeArgsSchema, (appointments: Appointment[], { dates }) => (
    <DiaryCard dateFrom={dates.date_from} dateTo={dates.date_to} appointments={appointments} />
  )),
  toolCard("get_availability", availabilityArgsSchema, (stylists: StaffAvailability[], { target_date }) => (
    <FreeTimeCard date={target_date} stylists={stylists} />
  )),
  toolCard("list_services", noArgsSchema, (stylists: StaffServicesSummary[]) => <PriceListCard stylists={stylists} />),
  defineToolCallRenderer({
    name: "*",
    render: ({ name, status }) => <CopilotStep name={name} complete={status === ToolCallStatus.Complete} />,
  }),
];
